import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../context/ApiContext';
import { useNavigate } from 'react-router-dom';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import { Dropdown, DropdownItem } from '../components/ui/Dropdown';
import { getSettings } from '../utils/settings';

export default function ApplicantsAdmin() {
  const { adminListApplicants, adminCreateApplicant, adminApproveApplicant, adminRejectApplicant, adminListProperties } = useApi();
  const navigate = useNavigate();
  const settings = useMemo(() => getSettings(), []);
  const [pages, setPages] = useState([]); // array of items per page (0-based)
  const [cursors, setCursors] = useState([null]); // cursor history; cursors[i] produces page i+1
  const [currentPage, setCurrentPage] = useState(1); // 1-based
  const [q, setQ] = useState('');
  const [property, setProperty] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const [issuing, setIssuing] = useState(null);
  const [properties, setProperties] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createForm, setCreateForm] = useState({
    propertySlug: '',
    fullName: '',
    email: '',
    phone: '',
    moveInDate: '',
    message: '',
  });
  // Drawer removed; using profile pages instead
  const hiddenApplicantStatuses = new Set(['approved', 'converted', 'declined']);

  useEffect(() => {
    resetAndFetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, property]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await adminListProperties();
        if (active) setProperties(Array.isArray(data) ? data : []);
      } catch {}
    })();
    return () => { active = false; };
  }, [adminListProperties]);

  function resetAndFetchFirstPage(){
    setPages([]);
    setCursors([null]);
    setCurrentPage(1);
    fetchPageByIndex(1, { force: true });
  }

  async function fetchPageByIndex(pageIndex, { force = false } = {}) {
    try {
      setLoading(true);
      setError('');
      // If we already have this page and not forcing, skip fetch
      if (!force && pages[pageIndex - 1]) {
        setCurrentPage(pageIndex);
        return;
      }
      const cursor = cursors[pageIndex - 1] || null;
      const res = await adminListApplicants({ limit: 25, cursor, q: q || undefined, property: property || undefined });
      const items = Array.isArray(res) ? res : (res?.items || []);
      const nextCursor = Array.isArray(res) ? null : (res?.nextCursor || null);
      setPages((prev) => {
        const copy = prev.slice();
        copy[pageIndex - 1] = items;
        return copy;
      });
      setCursors((prev) => {
        const copy = prev.slice();
        copy[pageIndex] = nextCursor; // cursor for next page
        return copy;
      });
      setCurrentPage(pageIndex);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function issueLease(id) {
    const monthlyPrice = window.prompt('Monthly price for lease (R):', '');
    const unitId = window.prompt('Assign Unit ID (optional):', '');
    try {
      setIssuing(id);
      await adminApproveApplicant({ appId: id, monthlyPrice: monthlyPrice ? Number(monthlyPrice) : undefined, unitId: unitId || undefined });
      alert('Lease issued and applicant moved to Tenants.');
      resetAndFetchFirstPage();
    } catch (e) {
      alert(e?.message || 'Could not approve');
    } finally {
      setIssuing(null);
    }
  }

  async function reject(id){
    try {
      await adminRejectApplicant(id);
      resetAndFetchFirstPage();
    } catch (e) {
      alert(e?.message || 'Could not reject');
    }
  }

  function openCreateModal() {
    setCreateError('');
    setCreateForm({
      propertySlug: property || '',
      fullName: '',
      email: '',
      phone: '',
      moveInDate: '',
      message: '',
    });
    setShowCreateModal(true);
  }

  function onCreateChange(e) {
    const { name, value } = e.target;
    setCreateForm((current) => ({ ...current, [name]: value }));
  }

  async function submitCreateApplicant() {
    try {
      setCreating(true);
      setCreateError('');
      const created = await adminCreateApplicant({
        propertySlug: createForm.propertySlug,
        fullName: createForm.fullName,
        email: createForm.email,
        phone: createForm.phone || undefined,
        moveInDate: createForm.moveInDate || undefined,
        message: createForm.message || undefined,
      });
      setShowCreateModal(false);
      setProperty(createForm.propertySlug || '');
      alert(`Applicant created: ${created.reference || created.id}`);
      resetAndFetchFirstPage();
      if (created?.id) {
        navigate(`/admin/applicants/${encodeURIComponent(created.id)}`);
      }
    } catch (e) {
      setCreateError(e?.message || 'Could not create applicant');
    } finally {
      setCreating(false);
    }
  }

  const currentItems = (pages[currentPage - 1] || []).filter((a) => {
    const status = String(a?.status || '').toLowerCase();
    return !hiddenApplicantStatuses.has(status);
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Applicants</h1>
        <p className="text-gray-600">Review and approve applications.</p>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <input className="border rounded px-3 py-2" placeholder="Search name/email/phone/ID" value={q} onChange={(e)=>setQ(e.target.value)} />
        <input className="border rounded px-3 py-2" placeholder="Filter by property slug" value={property} onChange={(e)=>setProperty(e.target.value)} />
        <Button onClick={() => resetAndFetchFirstPage()}>Refresh</Button>
        {settings.manualCreationEnabled && (
          <Button variant="primary" onClick={openCreateModal}>Add Manually</Button>
        )}
        <button className="px-3 py-2 border rounded" onClick={() => {
          const slug = window.prompt('Property slug for application invite (optional):', property || '');
          const url = `${window.location.origin}/apply-to-rent${slug ? `?property=${encodeURIComponent(slug)}` : ''}`;
          try { navigator.clipboard?.writeText(url); } catch {}
          alert(`Invite link copied:\n${url}`);
        }}>Invite Applicant</button>
        {loading && <span className="text-sm text-gray-500">Loading…</span>}
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {error}
          <div className="mt-2 flex gap-2">
            <button className="px-2 py-1 border rounded" onClick={() => resetAndFetchFirstPage()}>Retry</button>
            <button className="px-2 py-1 border rounded" onClick={async()=>{ try{ const h = await (await fetch('/api/health')).json(); setHealth(h);}catch(e){ setHealth({ error: e?.message || String(e) }); } }}>Check API Health</button>
          </div>
          {health && (<pre className="mt-2 text-xs overflow-auto max-h-64">{JSON.stringify(health,null,2)}</pre>)}
        </div>
      )}

      <div className="bg-white rounded-xl shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2">Applicant</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Property</th>
                <th className="text-left px-3 py-2">Docs</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((a) => {
                const docs = a.documents || [];
                const avatar = a.profilePhoto || (docs.find(d => typeof d?.name==='string' && /profile/i.test(d.name) && d.url)?.url) || (docs.find(d => (d?.fileName||d?.url||'').toLowerCase().match(/\.(jpg|jpeg|png|webp)$/) && d.url)?.url);
                const status = (a.status || 'submitted').toLowerCase();
                const statusVariant = status.includes('approved') ? 'green' : status.includes('declin') ? 'red' : (status.includes('review') ? 'blue' : 'amber');
                return (
                  <tr key={a.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        {avatar ? (
                          <img src={avatar} alt={a.fullName || a.form?.fullName} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200" />
                        )}
                        <div>
                          <button className="font-medium hover:underline" onClick={() => navigate(`/admin/applicants/${encodeURIComponent(a.id)}`)}>{a.fullName || a.form?.fullName}</button>
                          <div className="text-xs text-gray-500">ID: <button className="text-blue-600 hover:underline" onClick={() => navigate(`/admin/applicants/${encodeURIComponent(a.id)}`)}>{a.id}</button></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-800">{a.email || a.form?.email}</div>
                      <div className="text-xs text-gray-600">{a.phone || a.form?.phone}</div>
                    </td>
                    <td className="px-3 py-2"><Badge variant={statusVariant}>{a.status || 'Submitted'}</Badge></td>
                    <td className="px-3 py-2 text-xs text-gray-700">{a.propertySlug}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">{docs.filter(d=>d.url).length}/{docs.length || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{a.updatedAt ? new Date(a.updatedAt).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Dropdown>
                        <DropdownItem onClick={() => navigate(`/admin/applicants/${encodeURIComponent(a.id)}`)}>View Profile</DropdownItem>
                        <DropdownItem onClick={() => issueLease(a.id)}>{issuing === a.id ? 'Issuing…' : 'Approve & Issue Lease'}</DropdownItem>
                        <DropdownItem danger onClick={() => reject(a.id)}>Reject</DropdownItem>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
              {currentItems.length === 0 && (
                <tr className="border-t"><td className="px-3 py-8 text-center text-gray-600" colSpan={7}>No applicants found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mt-3">
        <Button disabled={currentPage === 1 || loading} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
          Prev
        </Button>
        <span className="text-sm text-gray-700">Page {currentPage}</span>
        <Button
          disabled={loading || (!pages[currentPage] && !cursors[currentPage])}
          onClick={() => {
            const nextPage = currentPage + 1;
            if (pages[nextPage - 1]) {
              setCurrentPage(nextPage);
            } else if (cursors[currentPage]) {
              fetchPageByIndex(nextPage);
            }
          }}
        >
          Next
        </Button>
      </div>

      {/* ProfileDrawer removed in favor of profile pages */}
      {showCreateModal && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/30" onClick={() => !creating && setShowCreateModal(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-xl p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Add Applicant Manually</h2>
                <p className="text-sm text-gray-600">This creates a real backend application record that can be assigned to a unit immediately.</p>
              </div>
              <Button disabled={creating} onClick={() => setShowCreateModal(false)}>Close</Button>
            </div>

            <div className="space-y-4">
              {createError && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {createError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Property</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  name="propertySlug"
                  value={createForm.propertySlug}
                  onChange={onCreateChange}
                >
                  <option value="">Select property…</option>
                  {properties.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.name} ({p.slug})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name</label>
                  <input className="w-full border rounded px-3 py-2" name="fullName" value={createForm.fullName} onChange={onCreateChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input className="w-full border rounded px-3 py-2" name="phone" value={createForm.phone} onChange={onCreateChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input className="w-full border rounded px-3 py-2" type="email" name="email" value={createForm.email} onChange={onCreateChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Desired Move-in Date</label>
                  <input className="w-full border rounded px-3 py-2" type="date" name="moveInDate" value={createForm.moveInDate} onChange={onCreateChange} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea className="w-full border rounded px-3 py-2" rows={4} name="message" value={createForm.message} onChange={onCreateChange} />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <Button disabled={creating} onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button variant="primary" disabled={creating} onClick={submitCreateApplicant}>
                {creating ? 'Creating…' : 'Create Applicant'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
